import React, { useState } from 'react';
import { ChatInterface } from './ChatInterface';
import { ReportsDashboard } from './reports/ReportsDashboard';
import { ReportEditor } from './reports/ReportEditor';
import { DigestDashboard } from './digests/DigestDashboard';
import { DigestEditor } from './digests/DigestEditor';
import { ConversationList } from './conversations/ConversationList';
import { CompassSettings } from './settings/CompassSettings';
import { Compass, MessageSquare, FileBarChart, History, PanelLeftClose, Settings, Mail } from 'lucide-react';
import { SavedReportDTO, DigestDTO } from '../../../../services/claudeQueryService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useSubModuleView } from '../../../../hooks/useSubModuleView';

type ViewMode = 'chat' | 'reports' | 'edit-report' | 'digests' | 'edit-digest' | 'settings';

interface ClaudeQueryAppProps {
    onBack?: () => void;
}

export const ClaudeQueryApp: React.FC<ClaudeQueryAppProps> = ({ onBack }) => {
    const { t } = useTranslation();
    const [activeView, setActiveView] = useSubModuleView<ViewMode>('chat', { landing: 'chat' });
    const [editingReport, setEditingReport] = useState<SavedReportDTO | null>(null);
    const [editingDigest, setEditingDigest] = useState<DigestDTO | null>(null);
    const [showHistory, setShowHistory] = useState(false);
    const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
    const [conversationKey, setConversationKey] = useState(0);
    const [reportInitialSQL, setReportInitialSQL] = useState<string | undefined>(undefined);
    const [reportInitialPrompt, setReportInitialPrompt] = useState<string | undefined>(undefined);
    const [reportInitialExportFormat, setReportInitialExportFormat] = useState<string | undefined>(undefined);
    const [reportInitialChartConfig, setReportInitialChartConfig] = useState<string | undefined>(undefined);

    // Report handlers
    const handleEditReport = (report: SavedReportDTO) => {
        setEditingReport(report);
        setActiveView('edit-report');
    };

    const handleCreateReport = () => {
        setEditingReport(null);
        setReportInitialSQL(undefined);
        setReportInitialPrompt(undefined);
        setReportInitialExportFormat(undefined);
        setReportInitialChartConfig(undefined);
        setActiveView('edit-report');
    };

    const handleCreateReportFromChat = (sql: string, prompt: string, exportFormat?: string, chartConfig?: string) => {
        setEditingReport(null);
        setReportInitialSQL(sql);
        setReportInitialPrompt(prompt);
        setReportInitialExportFormat(exportFormat);
        setReportInitialChartConfig(chartConfig);
        setActiveView('edit-report');
    };

    const handleReportSaved = () => {
        setEditingReport(null);
        setReportInitialSQL(undefined);
        setReportInitialPrompt(undefined);
        setReportInitialExportFormat(undefined);
        setReportInitialChartConfig(undefined);
        setActiveView('reports');
    };

    const handleCancelEdit = () => {
        setEditingReport(null);
        setReportInitialSQL(undefined);
        setReportInitialPrompt(undefined);
        setReportInitialExportFormat(undefined);
        setReportInitialChartConfig(undefined);
        setActiveView('reports');
    };

    // Digest handlers
    const handleEditDigest = (digest: DigestDTO) => {
        setEditingDigest(digest);
        setActiveView('edit-digest');
    };

    const handleCreateDigest = () => {
        setEditingDigest(null);
        setActiveView('edit-digest');
    };

    const handleDigestSaved = () => {
        setEditingDigest(null);
        setActiveView('digests');
    };

    const handleCancelDigestEdit = () => {
        setEditingDigest(null);
        setActiveView('digests');
    };

    // Conversation handlers
    const handleSelectConversation = (id: number) => {
        setActiveConversationId(id);
        setConversationKey(prev => prev + 1);
        setActiveView('chat');
    };

    const handleNewConversation = () => {
        setActiveConversationId(null);
        setConversationKey(prev => prev + 1);
        setActiveView('chat');
    };

    const handleConversationSaved = (id: number) => {
        setActiveConversationId(id);
    };

    const isMainView = activeView === 'chat' || activeView === 'reports' || activeView === 'digests';

    return (
        <div className="h-[calc(100vh-80px)] bg-gray-50 flex flex-col">
            {/* Header */}
            <div className="bg-white border-b px-6 py-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                            <Compass size={22} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-800">{t('manager.modules.claudeQuery.app.title', {}, 'Spør Compass')}</h1>
                            <p className="hidden lg:block text-xs text-gray-500">{t('manager.modules.claudeQuery.app.subtitle', {}, 'AI-drevet lagerassistent - still spørsmål i naturlig språk')}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* History toggle (only in chat view) */}
                        {activeView === 'chat' && (
                            <button
                                onClick={() => setShowHistory(!showHistory)}
                                className={`flex items-center gap-1.5 px-3 min-h-[44px] rounded-md text-sm transition-colors ${
                                    showHistory
                                        ? 'bg-blue-50 text-blue-600 border border-blue-200'
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                }`}
                                title={showHistory ? t('manager.modules.claudeQuery.app.hideHistory', {}, 'Skjul historikk') : t('manager.modules.claudeQuery.app.showHistory', {}, 'Vis historikk')}
                            >
                                {showHistory ? <PanelLeftClose size={14} /> : <History size={14} />}
                                <span className="hidden sm:inline">{t('manager.modules.claudeQuery.app.history', {}, 'Historikk')}</span>
                            </button>
                        )}

                        {/* Settings button */}
                        {isMainView && (
                            <button
                                onClick={() => setActiveView('settings')}
                                className="flex items-center gap-1.5 min-h-[44px] px-3 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                                aria-label={t('manager.modules.claudeQuery.app.settings', {}, 'Innstillinger')}
                            >
                                <Settings size={16} />
                                <span className="hidden xl:inline">{t('manager.modules.claudeQuery.app.settings', {}, 'Innstillinger')}</span>
                            </button>
                        )}

                        {/* Tab navigation */}
                        {isMainView && (
                            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                                <button
                                    onClick={() => setActiveView('chat')}
                                    className={`flex items-center gap-1.5 px-4 min-h-[44px] rounded-md text-sm font-medium transition-colors ${
                                        activeView === 'chat'
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <MessageSquare size={14} />
                                    {t('manager.modules.claudeQuery.app.tabChat', {}, 'Chat')}
                                </button>
                                <button
                                    onClick={() => setActiveView('reports')}
                                    className={`flex items-center gap-1.5 px-4 min-h-[44px] rounded-md text-sm font-medium transition-colors ${
                                        activeView === 'reports'
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <FileBarChart size={14} />
                                    {t('manager.modules.claudeQuery.app.tabReports', {}, 'Rapporter')}
                                </button>
                                <button
                                    onClick={() => setActiveView('digests')}
                                    className={`flex items-center gap-1.5 px-4 min-h-[44px] rounded-md text-sm font-medium transition-colors ${
                                        activeView === 'digests'
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <Mail size={14} />
                                    {t('manager.modules.claudeQuery.app.tabEmailDigests', {}, 'E-postsammendrag')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden flex">
                {/* Conversation sidebar */}
                {activeView === 'chat' && showHistory && (
                    <div className="w-72 flex-shrink-0 border-r border-gray-200 bg-white">
                        <ConversationList
                            activeConversationId={activeConversationId}
                            onSelectConversation={handleSelectConversation}
                            onNewConversation={handleNewConversation}
                            onClose={() => setShowHistory(false)}
                        />
                    </div>
                )}

                {/* Main content area */}
                <div className="flex-1 overflow-hidden p-3">
                    {activeView === 'chat' && (
                        <ChatInterface
                            key={conversationKey}
                            conversationId={activeConversationId}
                            onConversationSaved={handleConversationSaved}
                            onSaveAsReport={handleCreateReportFromChat}
                            onNewConversation={handleNewConversation}
                        />
                    )}
                    {activeView === 'reports' && (
                        <ReportsDashboard
                            onEditReport={handleEditReport}
                            onCreateReport={handleCreateReport}
                        />
                    )}
                    {activeView === 'edit-report' && (
                        <ReportEditor
                            report={editingReport}
                            onSave={handleReportSaved}
                            onCancel={handleCancelEdit}
                            initialSQL={reportInitialSQL}
                            initialPrompt={reportInitialPrompt}
                            initialExportFormat={reportInitialExportFormat}
                            initialChartConfig={reportInitialChartConfig}
                        />
                    )}
                    {activeView === 'digests' && (
                        <DigestDashboard
                            onEditDigest={handleEditDigest}
                            onCreateDigest={handleCreateDigest}
                        />
                    )}
                    {activeView === 'edit-digest' && (
                        <DigestEditor
                            digest={editingDigest}
                            onSave={handleDigestSaved}
                            onCancel={handleCancelDigestEdit}
                        />
                    )}
                    {activeView === 'settings' && (
                        <CompassSettings
                            onBack={() => setActiveView('chat')}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
